import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';

import { Stack } from '@mui/material';

import CustomIcon from 'assets/Icon';

import { useClientCommunicationContext } from 'context/clientCommunication.context';

import BasicModal from 'modules/shared/components/general/BasicModal/BasicModal';
import Spinner from 'modules/shared/components/general/Spinner/Spinner';
import SendEmailForOtherUsersModal from 'modules/ClientCommunication/components/SuccessFailedModals/SendEmailForOtherUsersModal';
import AreYouSureToSendModal from 'modules/ClientCommunication/components/SuccessFailedModals/AreYouSureToSendModal';
import ButtonWithGoogleAuth from 'modules/ClientCommunication/components/ButtonWithGoogleAuth/ButtonWithGoogleAuth';
import OwnerCard from 'modules/ClientCommunication/pages/BrandEmail/components/OwnerCard';
import EmailTemplate from 'modules/ClientCommunication/pages/BrandEmail/components/EmailTemplate';

import { getBrandEmailOwners } from 'services/reporting.services';

import Routes from 'constants/routes';
import { servicesNames } from 'constants/servicesNames.constants';

import './style.scss';

type BrandIdParams = {
    brand_id: string;
}

export default function BrandEmail() {
  const {
    areYouSureSendModalOpened,
    setAreYouSureSendModalOpened,
    setSendModalForOtherUser,
    sendModalForOtherUser,
  } = useClientCommunicationContext();

  const { brand_id } = useParams<BrandIdParams>();
  const navigate = useNavigate();

  const { data: brandEmailOwnersResponse, isLoading } = useQuery([servicesNames.getBrandEmailOwners, brand_id], {
    queryFn: () => (brand_id ? getBrandEmailOwners(brand_id) : null),
    enabled: Boolean(brand_id),
  });

  const onSuccessSendEmail = () => {
    navigate(Routes.MONTHLY_STRATEGY_UPDATE);
  };


  if (!brand_id) {
    navigate(Routes.MONTHLY_STRATEGY_UPDATE, { replace: true });
  }

  if (isLoading) {
    return (
      <Stack justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
        <Spinner />
      </Stack>
    );
  }

  if (!brandEmailOwnersResponse) {
    return null;
  }

  const { owners, brand_name, edit_required = true, ...emailTemplate } = brandEmailOwnersResponse.data;

  return (
    <div className="brand-email-container">
      <div className="brand-email-container-header">
        <h1 className="brand-email-container-header-title">{brand_name}</h1>
        {!emailTemplate.is_admin && (
          <ButtonWithGoogleAuth fsmEmail={emailTemplate.to_emails[0]} editRequired={edit_required} />
        )}
      </div>
      <div className="brand-email-container-body">
        <div className="brand-email-container-body-content">
          <div className="brand-email-container-body-owners">
            <div className="brand-email-container-body-owners-header">
              <div className="brand-email-container-body-owners-header-title">
                <CustomIcon name="user" style={{ color: '#444444' }} />
                <span>Owners</span>
              </div>
              <div className="brand-email-container-body-owners-header-sub_title">
                Send the Strategy Loom video to brand owners.
              </div>
            </div>
            <Stack spacing={1}>
              {owners.map(owner => <OwnerCard key={owner.id} owner={owner} />)}
            </Stack>
          </div>
          <EmailTemplate {...emailTemplate} />
        </div>
      </div>
      <BasicModal
        open={sendModalForOtherUser}
        handleClose={() => setSendModalForOtherUser(false)}
      >
        <SendEmailForOtherUsersModal
          onClose={() => setSendModalForOtherUser(false)}
          emailId={emailTemplate.client_email_id}
          onSuccess={onSuccessSendEmail}
        />
      </BasicModal>
      <BasicModal
        open={areYouSureSendModalOpened}
        handleClose={() => setAreYouSureSendModalOpened(false)}
      >
        <AreYouSureToSendModal
          onClose={() => setAreYouSureSendModalOpened(false)}
          emailId={emailTemplate.client_email_id}
          onSuccess={onSuccessSendEmail}
        />
      </BasicModal>
    </div>
  );
}
